import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api, { fmtErr, wsUrl } from "../lib/api";
import { toast } from "sonner";
import { Eye, Ghost, Zap, Briefcase, Check, ShieldCheck } from "lucide-react";
import { logSwallow } from "../lib/log";

export default function Home() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [modesBusy, setModesBusy] = useState(false);
  const [unread, setUnread] = useState(0);
  // Contextual hint state: exactly one control shows a floating notification
  // at a time. `key` = which anchor ('toggle' | 'scan-vibe' | 'scan-nearby'
  // | 'perks' | 'opportunity'). Auto-dismisses via a ref-tracked timer so the
  // next click can cancel it.
  const [hint, setHint] = useState({ key: null, text: null });
  const hintTimerRef = useRef(null);
  const showHint = (key, text, ms = 5500) => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    setHint({ key, text });
    hintTimerRef.current = setTimeout(() => setHint({ key: null, text: null }), ms);
  };
  useEffect(() => () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  // Load unread inbox count + subscribe to WS so the badge updates in realtime.
  // Deps: only user?.id. We deliberately don't depend on the full `user` object
  // because it re-renders on every profile field change (mode, avatar_url, etc.)
  // and would tear down/re-open the WS unnecessarily.
  useEffect(() => {
    if (!user) return;
    let ws = null;
    const load = async () => {
      try { const { data } = await api.get("/inbox"); setUnread(data.unread?.total || 0); }
      catch (e) { logSwallow("home.inbox.poll", e); }
    };
    load();
    const poll = setInterval(load, 20000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    try {
      const url = wsUrl("/user");
      if (url) {
        ws = new WebSocket(url);
        ws.onmessage = (ev) => {
          try {
            const m = JSON.parse(ev.data);
            if (["interest.received", "match.new", "vibe.comment", "message.new"].includes(m.type)) load();
          } catch (e) { logSwallow("home.ws.parse", e); }
        };
        ws.onerror = (e) => logSwallow("home.ws.error", e);
      }
    } catch (e) { logSwallow("home.ws.open", e); }
    return () => {
      clearInterval(poll);
      document.removeEventListener("visibilitychange", onVis);
      if (ws) { try { ws.close(); } catch (e) { logSwallow("home.ws.close", e); } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only re-run on user identity change
  }, [user?.id]);

  if (!user) return null;
  const isGhost = user.mode_default === "anonymous";
  const activeModes = new Set(user.active_modes || []);

  const MODE_HINTS = {
    visible: t("home.modeVisibleHint"),
    ghost: t("home.modeGhostHint"),
  };

  const setMode = async (mode) => {
    if (busy) return;
    // Fire the contextual hint IMMEDIATELY so the user sees feedback next to
    // the toggle they clicked, even before the API call resolves.
    showHint("toggle", MODE_HINTS[mode]);
    setBusy(true);
    try {
      const { data } = await api.post(`/profile/visibility?mode=${mode}`);
      setUser({ ...user, mode_default: data.mode });
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  // Scan buttons: two-tap pattern per user request.
  //   1st tap ever (per user, tracked in localStorage): show hint, do NOT navigate.
  //   2nd tap onwards: show hint AND navigate immediately (no artificial delay).
  //   The 5.5s auto-dismiss on the hint itself is unchanged.
  const hasSeenTapHint = (key) => {
    try { return !!localStorage.getItem(`bl_tap_seen_${key}`); }
    catch (e) { logSwallow("home.tap.seen.read", e); return false; }
  };
  const markTapHintSeen = (key) => {
    try { localStorage.setItem(`bl_tap_seen_${key}`, "1"); }
    catch (e) { logSwallow("home.tap.seen.write", e); }
  };
  const tapWithHint = (key, text, action) => {
    showHint(key, text);
    if (hasSeenTapHint(key)) {
      action();
    } else {
      markTapHintSeen(key);
    }
  };

  const CONTENT_MODE_HINTS = {
    perks: t("home.perksHint"),
    opportunity: t("home.opportunityHint"),
  };

  const applyContentMode = async (m) => {
    if (modesBusy) return;
    setModesBusy(true);
    const next = new Set(activeModes);
    next.has(m) ? next.delete(m) : next.add(m);
    try {
      const { data } = await api.post("/profile/modes", { modes: [...next] });
      setUser({ ...user, active_modes: data.modes });
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setModesBusy(false); }
  };

  const toggleContentMode = (m) => {
    // Two-tap: first tap teaches (hint only), subsequent taps actually toggle
    tapWithHint(m, CONTENT_MODE_HINTS[m], () => applyContentMode(m));
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      {/* Top red glow */}
      <div className="absolute top-0 inset-x-0 h-[60vh] bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.25)_0%,transparent_60%)] pointer-events-none" />

      <div className="relative max-w-md mx-auto px-6 pt-14 pb-12 flex flex-col items-center">
        {/* Eye logo */}
        <EyeLogo />

        {/* Wordmark */}
        <h1
          data-testid="brand-wordmark"
          className="mt-6 text-5xl sm:text-6xl font-display tracking-[-0.03em] text-white text-center leading-none"
          style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, letterSpacing: "-0.04em" }}
        >
          BLINK LINK
        </h1>
        <p className="mt-3 font-mono-accent text-zinc-500 text-center">{t("home.tagline")}</p>

        {/* Visibility toggle with contextual hint anchored above */}
        <div className="mt-8 relative w-full flex justify-center" data-testid="visibility-toggle-wrap">
          <ContextualHint show={hint.key === "toggle"} text={hint.text} anchorKey="toggle" />
          <div className="inline-flex p-1.5 rounded-full bg-black/60 border border-white/10 backdrop-blur-xl" data-testid="visibility-toggle">
            <button
              onClick={() => setMode("visible")}
              data-testid="mode-visible"
              disabled={busy}
              className={`px-6 py-2.5 rounded-full flex items-center gap-2 transition-all text-sm font-mono-accent ${
                !isGhost
                  ? "bg-gradient-to-r from-[#E60000] to-[#990000] text-white shadow-[0_0_20px_rgba(230,0,0,0.5)]"
                  : "text-zinc-500 hover:text-white"
              }`}
            >
              <Eye size={14} /> {t("home.visible")}
            </button>
            <button
              onClick={() => setMode("ghost")}
              data-testid="mode-ghost"
              disabled={busy}
              className={`px-6 py-2.5 rounded-full flex items-center gap-2 transition-all text-sm font-mono-accent ${
                isGhost
                  ? "bg-gradient-to-r from-[#E60000] to-[#990000] text-white shadow-[0_0_20px_rgba(230,0,0,0.5)]"
                  : "text-zinc-500 hover:text-white"
              }`}
            >
              <Ghost size={14} /> {t("home.ghost")}
            </button>
          </div>
        </div>

        {/* SHOW ME — content discovery modes (independent toggles) */}
        <div className="mt-5 w-full max-w-xs flex flex-col items-center">
          <p className="font-mono-accent text-zinc-600 tracking-[0.3em] text-[0.65rem]" data-testid="modes-label">{t("home.showMe")}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 w-full" data-testid="content-modes">
            <div className="relative">
              <ContextualHint show={hint.key === "perks"} text={hint.text} anchorKey="perks" />
              <ContentModeChip
                testid="mode-perks"
                icon={Zap}
                label={t("home.perks")}
                sub={t("home.perksSub")}
                active={activeModes.has("perks")}
                onClick={() => toggleContentMode("perks")}
                disabled={modesBusy}
              />
            </div>
            <div className="relative">
              <ContextualHint show={hint.key === "opportunity"} text={hint.text} anchorKey="opportunity" />
              <ContentModeChip
                testid="mode-opportunity"
                icon={Briefcase}
                label={t("home.opportunity")}
                sub={t("home.opportunitySub")}
                active={activeModes.has("opportunity")}
                onClick={() => toggleContentMode("opportunity")}
                disabled={modesBusy}
              />
            </div>
          </div>
        </div>

        {/* CTAs */}
        <div className="w-full mt-10 space-y-4">
          <div className="relative">
            <ContextualHint show={hint.key === "scan-vibe"} text={hint.text} anchorKey="scan-vibe" />
            <HomeButton
              testid="home-scan-vibe"
              title={t("home.scanVibe")}
              subtitle={t("home.scanVibeSub")}
              onClick={() => tapWithHint(
                "scan-vibe",
                t("home.scanVibeHint"),
                () => nav("/live-room"),
              )}
            />
          </div>
          <div className="relative">
            <ContextualHint show={hint.key === "scan-nearby"} text={hint.text} anchorKey="scan-nearby" />
            <HomeButton
              testid="home-scan-nearby"
              title={t("home.scanNearby")}
              subtitle={t("home.scanNearbySub")}
              onClick={() => tapWithHint(
                "scan-nearby",
                t("home.scanNearbyHint"),
                () => nav("/scan-nearby"),
              )}
            />
          </div>
          <HomeButton
            testid="home-inbox"
            title={t("home.inbox")}
            subtitle={t("home.inboxSub")}
            badge={unread}
            onClick={() => nav("/inbox")}
          />
          <HomeButton
            testid="home-photo-vault"
            title={t("home.photoVault")}
            subtitle={t("home.photoVaultSub")}
            onClick={() => nav("/vault/pin")}
          />
          <HomeButton
            testid="home-settings"
            title={t("home.settings")}
            subtitle={t("home.settingsSub")}
            onClick={() => nav("/settings")}
          />
          {(user.role === "admin" || user.role === "moderator") && (
            <button
              onClick={() => nav("/admin")}
              data-testid="home-admin-dash"
              className="w-full py-6 rounded-3xl bg-gradient-to-b from-[#E60000]/15 to-black/40 border border-[#E60000]/50 hover:border-[#E60000] hover:shadow-[0_0_30px_rgba(230,0,0,0.35)] transition-all duration-300 text-center active:scale-[0.98] relative"
            >
              <span className="absolute top-3 right-4 flex items-center gap-1 text-[9px] font-mono-accent tracking-[0.2em] text-[#E60000]">
                <ShieldCheck size={11} /> ADMIN
              </span>
              <div className="text-xl font-bold tracking-wider text-white">{t("home.adminDash")}</div>
              <div className="text-xs text-zinc-400 mt-1.5 font-light">{t("home.adminDashSub")}</div>
            </button>
          )}
        </div>

        <p className="mt-12 font-mono-accent text-zinc-600 text-center text-[0.65rem]" data-testid="brand-footer">
          {t("home.footer")}
        </p>
      </div>
    </div>
  );
}

/**
 * ContextualHint — floating notification anchored above whatever control
 * spawned it. Styled to match the sonner "Ghost mode on" toast (dark glass
 * background + white check chip + subtle red border glow) but pinned to the
 * control instead of the top of the screen.
 *
 * Positioning: absolute, bottom = 100% of parent + gap → the tip's arrow
 * points down to the parent. `pointer-events-none` so the hint never blocks
 * a tap on the button it labels. Auto-dismiss is handled by the parent.
 */
function ContextualHint({ show, text, anchorKey }) {
  return (
    <div
      className={`absolute left-1/2 bottom-full mb-3 -translate-x-1/2 z-30 pointer-events-none transition-all duration-300 ease-out ${
        show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
      }`}
      style={{ width: "min(340px, calc(100vw - 2rem))" }}
      data-testid={`contextual-hint-${anchorKey}`}
      data-visible={show ? "true" : "false"}
      aria-live="polite"
    >
      <div className="rounded-2xl border border-white/10 bg-black/85 backdrop-blur-xl shadow-[0_0_28px_rgba(230,0,0,0.25)] px-3.5 py-2.5 flex items-start gap-3">
        <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center">
          <Check size={12} className="text-black" strokeWidth={3} />
        </span>
        <p className={`text-white text-[0.82rem] leading-snug ${show ? "" : "select-none"}`}>{text || ""}</p>
      </div>
      {/* Arrow pointing down to the anchored control */}
      <div className="mx-auto -mt-1 w-2.5 h-2.5 rotate-45 bg-black/85 border-r border-b border-white/10" />
    </div>
  );
}

function HomeButton({ title, subtitle, onClick, testid, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="w-full py-6 rounded-3xl bg-black/40 border border-white/10 hover:border-[#E60000]/60 hover:bg-black/70 hover:shadow-[0_0_30px_rgba(230,0,0,0.15)] transition-all duration-300 text-center group active:scale-[0.98] relative"
    >
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute top-3 right-4 min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#E60000] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_12px_rgba(230,0,0,0.7)]"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
      <div className="text-xl font-bold tracking-wider text-white">{title}</div>
      {subtitle && <div className="text-xs text-zinc-500 mt-1.5 font-light">{subtitle}</div>}
    </button>
  );
}

function ContentModeChip({ icon: Icon, label, sub, active, onClick, disabled, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-pressed={active}
      className={`py-3 px-3 rounded-2xl border transition-all active:scale-[0.97] flex flex-col items-center gap-1 ${
        active
          ? "border-[#E60000] bg-gradient-to-b from-[#E60000]/20 to-[#660000]/10 shadow-[0_0_20px_rgba(230,0,0,0.35)]"
          : "border-white/10 bg-black/40 hover:border-white/30"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <Icon size={13} className={active ? "text-[#E60000]" : "text-zinc-400"} />
        <span className={`font-mono-accent text-[0.7rem] tracking-[0.18em] ${active ? "text-white" : "text-zinc-400"}`}>
          {label}
        </span>
      </div>
      <span className="text-[0.6rem] text-zinc-500 font-light tracking-wide">{sub}</span>
    </button>
  );
}

function EyeLogo() {
  return (
    <div className="w-36 h-24 flex items-center justify-center" data-testid="brand-logo">
      <svg viewBox="0 0 200 120" className="w-full h-full">
        <ellipse cx="100" cy="60" rx="92" ry="50" fill="#ffffff" />
        <circle cx="100" cy="60" r="34" fill="#0b0202" stroke="#330000" strokeWidth="2" />
        <path
          d="M100 80 C 90 70, 78 64, 78 52 C 78 44, 86 38, 92 42 C 96 44, 99 48, 100 52 C 101 48, 104 44, 108 42 C 114 38, 122 44, 122 52 C 122 64, 110 70, 100 80 Z"
          fill="#E60000"
        />
      </svg>
    </div>
  );
}
